import { z } from "zod";

import { findPermission } from "../permissions";
import type { RecordAccessLevel } from "./recordAccess";

/**
 * SÖZLEŞME KATILIMCI ROL PRESETLERİ (CLOS-149 `[130]`).
 *
 * Preset bir KULLANICI DENEYİMİ şablonudur, yetki katmanı DEĞİL: gerçek yetki CLOS-83/84
 * (permission motoru) ve CLOS-91/93 (kayıt erişimi) kararlarıyla uygulanır. Preset yalnız
 * "bu tür katılımcıya şu açık seti ver" önerisini adlandırır; ataması yapılırken açık
 * sete çözülür ve tarihsel erişimler preset değişse de DEĞİŞMEZ (satırlar presete
 * referans tutmaz — yapısal koruma).
 */

export const PARTICIPANT_TYPES = ["internal", "external_person", "external_organization"] as const;
export type ParticipantType = (typeof PARTICIPANT_TYPES)[number];

/**
 * Katılımcı türü başına izin verilen permission TAVANI.
 *
 * ⚠ Haricî tavanlar KODDA ve DAR — kart: *"haricî roller organizasyon yönetimi,
 * abonelik, audit yönetimi veya başka sözleşmelere erişim VEREMEZ."* Konfige taşımak,
 * güvenlik sınırını bir dosya düzenlemesine açmak olurdu; bu sınır ancak kartla genişler.
 * `contract.revision` ARTIK haricî yüzeyde tüketiliyor (CLOS-873, 2 Eylül 2026): paylaşılan
 * sözleşme ekranında karşı tarafın öneri geri çekme yüzeyi bu anahtarla kapılı. Not bir
 * süre "bugün tüketilmiyor" diyordu ve tavanın ileriye dönük tutulmasını savunuyordu;
 * savunma tuttu — preset tanımı tüketici yüzeyden önce vardı ve yüzey geldiğinde hiçbir
 * presetin elden geçmesi gerekmedi.
 *
 * ⚠ TAVAN, UÇ KAPISININ KAYNAĞI DEĞİL. Tavan sözleşme rol PRESETLERİNİ sınırlıyor;
 * paylaşılan uçlardaki `requirePermission` ise çağıranın KENDİ alanındaki rolüne bakıyor
 * ve katılım satırının `permissions`/`accessLevel` alanlarını hiç okumuyor (CLOS-873'te
 * ölçüldü). Üç ayrı eksen: ne yapabilirim (kendi rolüm) · hangi kayda (sahip bağlamında
 * kayıt erişimi) · hangi malzeme üzerinde (açık nesne paylaşımı).
 */
export const EXTERNAL_PARTY_CEILING: readonly string[] = [
  "contract.view",
  "contract.comment",
  "contract.revision",
  "contract.review",
  "contract.approve",
  "contract.reconcile",
  "contract.sign",
];
/** Varsayılan dış taraf kapsamı (Halit S3): yalnız görüntüleme + revizyon önerme. */
export const EXTERNAL_PARTY_DEFAULT_PERMISSIONS: readonly string[] = [
  "contract.view",
  "contract.revision",
];

/**
 * DIŞ TARAF BAŞLANGIÇ PRESETLERİ (Mega E2E GAP-002).
 *
 * ### ⚠ NEDEN VAR: tavan kodda açıktı, arayüzde kapalıydı
 *
 * Canlı ölçümde davet penceresindeki "Erişim kapsamı" seçicisinde TEK seçenek vardı —
 * varsayılan "Görüntüleme + Revizyon önerme". Sebep bir kusur değil, bir eksiklikti: seçici
 * kiracının TANIMLI presetlerini listeliyor ve yeni kiracıda hiç preset doğmuyordu.
 * `EXTERNAL_PARTY_CEILING` yedi yetenek ilan ederken arayüz ikisini verebiliyordu; ortak
 * yorum, mutabakat ve imza fazları karşı taraf organizasyonuyla hiç koşulamıyordu.
 *
 * ### ⚠ NEDEN PRESET, NEDEN CHECKBOX MATRİSİ DEĞİL
 *
 * Davet penceresinin kararı yazılı: *"burada yalnız DIŞ KAPSAM seçilir — checkbox matrisi
 * yok"*. Kapsamı davet anında yetenek yetenek işaretletmek, hukuki bir tarafa ne
 * açtığını adlandırılmamış bir kümeye çevirirdi. Bu liste yeni bir kavram getirmiyor;
 * tavanı ADLANDIRIYOR.
 *
 * ### ⚠ İŞ AKIŞLARI BİRBİRİNE EŞİTLENMİYOR
 *
 * İnceleme, onay, mutabakat ve imza AYRI workflow'lar. Bu yüzden kümülatif tek bir merdiven
 * yok: her preset ortak çalışma tabanına (görüntüleme + yorum + revizyon) YALNIZ kendi
 * workflow anahtarını ekliyor. "İmza yetkili" preseti mutabakat yetkisi taşımıyor; ikisi
 * gerekiyorsa kiracı kendi presetini tanımlar.
 *
 * ⚠ PRESET YETKİ DEĞİL: gerçek yetki `tavan ∩ alıcı iç yetki ∩ aktif üyelik ∩ kayıt
 * erişimi ∩ workflow authority` kesişiminde doğar. Bu liste yalnız daveti kuran kişiye
 * "ne kadarını açıyorum" sorusunu sorulabilir kılıyor.
 */
export const EXTERNAL_PARTY_STARTER_PRESETS: readonly {
  name: string;
  description: string;
  permissions: readonly string[];
}[] = [
  {
    name: "Görüntüleme + Yorum",
    description: "Sözleşmeyi okur ve ortak yorum yüzeyinde konuşabilir; metne dokunamaz.",
    permissions: ["contract.view", "contract.comment"],
  },
  {
    name: "Ortak çalışma",
    description: "Okur, yorum yazar ve revizyon önerir. Öneri kabulü karşı tarafta kalır.",
    permissions: ["contract.view", "contract.comment", "contract.revision"],
  },
  {
    name: "İnceleme yetkili",
    description: "Ortak çalışmaya ek olarak kendisine düşen inceleme görevini karara bağlar.",
    permissions: ["contract.view", "contract.comment", "contract.revision", "contract.review"],
  },
  {
    name: "Onay yetkili",
    description: "Ortak çalışmaya ek olarak kendi tarafının onay adımını karara bağlar.",
    permissions: ["contract.view", "contract.comment", "contract.revision", "contract.approve"],
  },
  {
    name: "Mutabakat yetkili",
    description: "Ortak çalışmaya ek olarak kendi tarafı adına mutabakat kararı verebilir.",
    permissions: ["contract.view", "contract.comment", "contract.revision", "contract.reconcile"],
  },
  {
    name: "İmza yetkili",
    description: "Ortak çalışmaya ek olarak kendi tarafı adına imzalayabilir.",
    permissions: ["contract.view", "contract.comment", "contract.revision", "contract.sign"],
  },
];

export const PARTICIPANT_PERMISSION_CEILINGS: Record<ParticipantType, readonly string[]> = {
  /** İç katılımcı: sözleşme domaininin tamamı — aktör sınırı ayrıca motorda doğrulanır. */
  internal: [
    "contract.view",
    "contract.create",
    "contract.edit",
    "contract.share",
    "contract.review",
    "contract.revision",
    "contract.archive",
  ],
  /**
   * DIŞ TARAF TAVANI (CLOS-1125 / Halit S3): sistemin bir hukuki tarafa AÇABİLECEĞİ en geniş
   * küme. Varsayılan preset yalnız Görüntüleme + Revizyon önerme; review/approve/reconcile/sign
   * ancak `tavan ∩ alıcı iç yetki ∩ aktif üyelik ∩ kayıt erişimi ∩ workflow authority`
   * kesişiminde çalışır (`requireParticipantCapability`). Anahtarlar KATALOGDAKİ gerçek
   * anahtarlar; Review / Approval / Reconcile / Sign ayrı workflow'lar, birbirine eşitlenmez.
   */
  external_person: EXTERNAL_PARTY_CEILING,
  external_organization: EXTERNAL_PARTY_CEILING,
};

/**
 * Tavan denetimi — tür için izin verilmeyen anahtarlar.
 *
 * ⚠ `protected` risk sınıfı HARİCÎ türlerde tavana bakılmaksızın yasak (kart: korunan
 * workflow/imza permission'ları rol etiketiyle bypass edilemez). Tavan listeleri bugün
 * protected anahtar içermiyor; bu kontrol, tavana yarın eklenen bir anahtarın sınıfı
 * gözden kaçarsa ikinci duvar.
 */
export function disallowedPresetPermissions(
  participantType: ParticipantType,
  permissions: readonly string[],
): string[] {
  const tavan = PARTICIPANT_PERMISSION_CEILINGS[participantType];
  return permissions.filter((anahtar) => {
    if (!tavan.includes(anahtar)) return true;
    if (participantType !== "internal") {
      const giris = findPermission(anahtar);
      if (giris?.risk === "protected") return true;
    }
    return false;
  });
}

/**
 * Presetten CLOS-91 erişim seviyesine ÇÖZÜMLEME.
 *
 * Saklanmıyor, TÜRETİLİYOR: ayrı bir kolonda tutulsaydı permissions ile bir gün ayrışır
 * ve preset "görüntüleme" deyip yazma erişimi verirdi. Kural tek ve okunur: sette
 * `contract.edit` varsa `write`, yoksa `read`.
 */
export function resolvePresetAccessLevel(permissions: readonly string[]): RecordAccessLevel {
  return permissions.includes("contract.edit") ? "write" : "read";
}

/** Presetin görünür RİSK sınıfı — en yüksek permission risk sınıfından türetilir. */
export function resolvePresetRiskClass(permissions: readonly string[]): string {
  const siralama = ["standard", "sensitive", "protected"] as const;
  let enYuksek = 0;
  for (const anahtar of permissions) {
    const giris = findPermission(anahtar);
    const indeks = giris ? siralama.indexOf(giris.risk as (typeof siralama)[number]) : 0;
    if (indeks > enYuksek) enYuksek = indeks;
  }
  return siralama[enYuksek]!;
}

export const createContractRolePresetSchema = z.strictObject({
  name: z
    .string()
    .trim()
    .min(1, "Preset adı zorunludur")
    .max(80, "Preset adı en fazla 80 karakter olabilir"),
  description: z.string().trim().max(500, "Açıklama en fazla 500 karakter olabilir").optional(),
  participantType: z.enum(PARTICIPANT_TYPES, "Geçerli bir katılımcı türü seçilmelidir"),
  permissions: z
    .array(z.string().min(1))
    .min(1, "En az bir yetki seçilmelidir")
    .max(20, "En fazla 20 yetki seçilebilir"),
  /**
   * Görünürlük SINIRI: `internal` presetin verdiği yüzeyler yalnız iç ekiplere görünür;
   * `shared` karşı tarafla paylaşılan yüzeylerde de kullanılabilir. Tanım alanıdır —
   * uygulaması tüketici kartların işi.
   */
  visibility: z.enum(["internal", "shared"], "Geçerli bir görünürlük seçilmelidir"),
});
export type CreateContractRolePresetInput = z.infer<typeof createContractRolePresetSchema>;

export const updateContractRolePresetSchema = createContractRolePresetSchema
  .omit({ participantType: true })
  .extend({
    /** Okunan sürüm — eşzamanlı düzenlemede sessiz ezme yerine 409 (müvekkil deseni). */
    version: z.coerce.number().int().min(1),
  });
export type UpdateContractRolePresetInput = z.infer<typeof updateContractRolePresetSchema>;

export type ContractRolePresetView = {
  id: string;
  name: string;
  description: string | null;
  participantType: ParticipantType;
  permissions: string[];
  visibility: "internal" | "shared";
  /** Türetilmiş projection'lar — saklanmaz, ayrışamaz. */
  accessLevel: RecordAccessLevel;
  riskClass: string;
  version: number;
  archivedAt: string | null;
  createdByName: string;
  createdAt: string;
};
